import React, { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronLeft, Loader2, Store, BadgeCheck, Search, Globe, MapPin, PackageOpen } from "lucide-react";
import { MKT_GOLD, MKT_TINT } from "../../lib/marketplace";
import { apiPublicTienda } from "../../lib/marketplaceApi";
import { CatalogCard, catLabel } from "./publicUi";

export default function TiendaCatalogo() {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(undefined);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("");

  useEffect(() => { apiPublicTienda(storeId).then(setData).catch(() => setData(null)); }, [storeId]);

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center text-slate-500"><Loader2 className="animate-spin mr-2" /> Cargando…</div>;
  if (!data) return <div className="min-h-screen flex flex-col items-center justify-center gap-3"><p className="text-slate-500">Tienda no disponible.</p><Link to="/tienda" className="font-bold" style={{ color: MKT_GOLD }}>Volver al Marketplace</Link></div>;

  const t = data.tienda;
  const categorias = Array.from(new Set((data.items || []).map((i) => i.categoria)));
  const items = (data.items || []).filter((i) => (!cat || i.categoria === cat) && (!q || (i.titulo || "").toLowerCase().includes(q.toLowerCase())));

  return (
    <div className="min-h-screen bg-slate-50" data-testid="tienda-catalogo">
      <div className="relative">
        <div className="h-44 md:h-56" style={{ background: t.portada ? undefined : `linear-gradient(135deg, ${MKT_GOLD}, #B45309)` }}>
          {t.portada && <img src={t.portada} alt="portada" className="w-full h-full object-cover" />}
        </div>
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <button onClick={() => navigate("/tienda")} className="absolute top-4 left-6 inline-flex items-center gap-1 text-sm font-semibold text-white bg-black/30 rounded-full px-3 py-1.5 hover:bg-black/50"><ChevronLeft size={16} /> Marketplace</button>
          <div className="flex flex-col sm:flex-row sm:items-end gap-4 -mt-12 pb-2">
            <div className="w-24 h-24 rounded-2xl border-4 border-white bg-white shadow-md overflow-hidden flex items-center justify-center flex-shrink-0">
              {t.logo ? <img src={t.logo} alt="logo" className="w-full h-full object-cover" /> : <Store size={34} style={{ color: MKT_GOLD }} />}
            </div>
            <div className="flex-1">
              <h1 className="text-2xl md:text-3xl font-extrabold text-slate-900 inline-flex items-center gap-2">{t.nombre} {t.verificado && <span className="inline-flex items-center gap-1 text-xs font-bold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-1"><BadgeCheck size={13} /> Vendedor verificado</span>}</h1>
              <p className="text-sm text-slate-500 mt-0.5">por {t.vendedor}</p>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {t.descripcion && <p className="text-slate-600 max-w-3xl">{t.descripcion}</p>}
        <div className="mt-3 flex flex-wrap items-center gap-5 text-sm text-slate-600">
          {(t.ciudad || t.pais) && <span className="inline-flex items-center gap-1.5"><MapPin size={15} style={{ color: MKT_GOLD }} /> {[t.ciudad, t.pais].filter(Boolean).join(", ")}</span>}
          {t.web && <a href={t.web} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 hover:text-amber-700"><Globe size={15} style={{ color: MKT_GOLD }} /> Portafolio</a>}
        </div>

        <div className="mt-6 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar en esta tienda…" data-testid="tienda-buscador" className="w-full rounded-xl border border-slate-300 pl-9 pr-3 py-2 text-sm outline-none focus:border-amber-500" />
          </div>
          <button onClick={() => setCat("")} className="px-3 py-1.5 rounded-full text-xs font-semibold border" style={cat === "" ? { backgroundColor: MKT_GOLD, borderColor: MKT_GOLD, color: "#fff" } : { borderColor: "#E2E8F0", color: "#475569" }}>Todas</button>
          {categorias.map((c) => (
            <button key={c} onClick={() => setCat(c)} className="px-3 py-1.5 rounded-full text-xs font-semibold border" style={cat === c ? { backgroundColor: MKT_GOLD, borderColor: MKT_GOLD, color: "#fff" } : { borderColor: "#E2E8F0", color: "#475569" }}>{catLabel(c)}</button>
          ))}
        </div>

        <div className="mt-6">
          {items.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="tienda-items">
              {items.map((i) => <CatalogCard key={`${i.tipo}-${i.id}`} item={i} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-slate-400"><PackageOpen size={40} className="mx-auto" /><p className="mt-2">Esta tienda aún no tiene contenido publicado.</p></div>
          )}
        </div>
      </div>
    </div>
  );
}
